/**
 * oneclick.ts — the manage dialog's button state machine, as pure functions.
 *
 * WHY A MODULE. Gate 5 (vitest) exists in `scripts/verify.sh` because a state bug
 * that lived inside `main.ts` was structurally unreachable by pytest and shipped
 * green through the other four gates (a managed short's `side` leaked onto the next
 * market and drew a SHORT's liquidation ABOVE a LONG's entry). A dialog that can
 * send an order in ONE press is precisely that class of state, so the decision of
 * WHICH buttons are live lives here where a test can drive it.
 *
 * WHAT IT ENCODES. `modify-sl` and `modify-tp` collapse to a single press
 * (`/api/trade/modify`, which is the whole write — no dry-run stage at all). `place`
 * keeps the two-phase Dry-run → Confirm pair, and the reason is a measurement of the
 * CLI rather than a preference: a verb may collapse only once its risk gates run ABOVE
 * `manage.py`'s `--confirm` branch, so that removing the preview removes a *preview*
 * and not the last check — EXCEPT for the closing verbs, where the asymmetry below
 * makes the preview a preview whether or not a gate exists.
 *
 * `modify-tp` joined on 2026-07-28 and only in that order. Andrei asked for it ("when
 * adding take profit, we don't need dry run"), and `_modify_tp` had **zero** risk-gate
 * calls: measured live on the `xyz:INTC` 5x long at entry 88, `modify-tp --tp 50` — a
 * guaranteed loss booked as a "profit" — was ACCEPTED, so the dry-run WAS the only
 * check. RULE 11 (a sign test: long TP above entry, short below, never forceable) was
 * added first; the collapse followed. `_close` and `_cancel` still have no gate, which
 * is exactly why they are still two-phase.
 *
 * The whitelist is duplicated from the backend's `_MODIFY_VERBS` deliberately: the
 * server refuses anything else with a 400, so this copy can only ever hide a button,
 * never enable a write the backend would reject.
 *
 * `cancel` LEFT THE DIALOG ENTIRELY on 2026-08-13 (Andrei: "it should allow canceling
 * orders immidiately without any additional checks"). It is not in `ONE_CLICK_VERBS`
 * -- the backend has no single-request route for it -- but in `NO_CONFIRM_VERBS`
 * below, which is a different question: not "does one REQUEST do it" but "may one
 * CLICK send it with no modal". A cancel only ever removes a resting order, so the
 * dry-run it skips was a preview of a risk-reducing act, not a check. Everything
 * else still opens the dialog and still needs its confirm. See `NO_CONFIRM_VERBS`
 * for why that is a set and not a flag.
 */

/** Which verbs the backend will complete in one request. Mirrors `_MODIFY_VERBS`. */
export const ONE_CLICK_VERBS: ReadonlySet<string> = new Set(['modify-sl', 'modify-tp']);

export const isOneClick = (verb: string): boolean => ONE_CLICK_VERBS.has(verb);

/**
 * NO DIALOG AT ALL: verbs a table button may send on ONE click, with no dry-run,
 * no confirm and no modal. Exactly one verb, and the reason is an asymmetry
 * rather than a preference.
 *
 * A CANCEL IS THE ONLY MUTATION HERE THAT CAN ONLY EVER REDUCE RISK. It removes a
 * resting order: it cannot open a position, cannot increase exposure, and cannot
 * lose money by being wrong -- the worst outcome is that Andrei re-places an order
 * he wanted. §0b.2 puts the gate at the ORDER-PLACEMENT boundary, and a cancel is
 * the far side of it.
 *
 * WHY THIS IS A SET AND NOT A FLAG. `close` also reduces a position, and that is
 * exactly the argument that must not be allowed to smuggle it in: a close REALISES
 * PnL and is not undone by re-placing an order, and `modify-sl` / `modify-tp` /
 * `place` change or create exposure. If this were a `skipConfirm: true` option on
 * the shared dialog, one future call site could pass it for `close`. Membership is
 * the check, so widening it means editing this line and the test that pins it.
 *
 * NOT the same set as `ONE_CLICK_VERBS` above, and deliberately disjoint from it:
 * that one is "the backend finishes it in a single request" (`/api/trade/modify`),
 * which `cancel` is not -- the cancel path still does the plan and the execute
 * round trips, it just does both inside the one click without showing a token to a
 * user who cannot act on it.
 */
export const NO_CONFIRM_VERBS: ReadonlySet<string> = new Set(['cancel']);

export const isNoConfirm = (verb: string): boolean => NO_CONFIRM_VERBS.has(verb);

/**
 * THE DIALOG OPENS AND ONE PRESS OF CONFIRM SENDS IT: no Dry-run stage at all.
 *
 * Andrei, 2026-08-25, on the "Close position (100%, MARKET) — xyz:MSFT" dialog:
 * *"закрывать сразу без дополнительных проверок на бекенде, но с подтверждением
 * пользователя. то есть по этому диалогу надо просто дать возможность нажать confirm
 * и тут же отменить ордер."* So the human step STAYS and the preview goes.
 *
 * WHY THAT IS NOT A WEAKENING, and the argument is the one `cancel` already won on
 * 2026-08-13. `exec_tools/cli/manage.py::_close` runs NO risk gate — measured, not
 * remembered: its only refusals are argument validation (`--size`/`--pct` mutually
 * exclusive, `--pct` in (0, 100], `--size` positive and not larger than the position),
 * and those live ABOVE the `--confirm` branch, so they run on the confirmed write too.
 * What the dry-run showed was therefore a PREVIEW of a risk-REDUCING act, never the
 * last check. A close only ever shrinks exposure, so §0b.2's gate — which sits at the
 * order-PLACEMENT boundary — is on the other side of it.
 *
 * WHY `close` IS HERE AND NOT IN `NO_CONFIRM_VERBS`. A close REALISES PnL and is not
 * undone by re-placing an order, which is exactly why it does not get the dialog-free
 * treatment a cancel gets. One press, but a press the user had to make in a modal that
 * names the symbol and the percentage.
 *
 * WHY IT IS A SET AND NOT A FLAG, same as above: `place` OPENS exposure and its dry-run
 * is the check (RULE 4's bracket rollback, RULE 5 through
 * `risk_gates`), not a preview. If this were `skipDryRun: true` on the shared dialog,
 * one future call site could pass it for `place`. Membership is the check.
 *
 * THE CLOSING VERBS ARE `close` AND `cancel`, and that is the whole list — checked
 * against `trade_actions.VERBS`, which offers exactly `close`, `cancel` and `place`.
 * `cancel` reaches its one press through `NO_CONFIRM_VERBS` (no modal at all), so this
 * set holds the one closing verb that still shows a dialog.
 */
export const NO_DRYRUN_VERBS: ReadonlySet<string> = new Set(['close']);

export const isNoDryRun = (verb: string): boolean => NO_DRYRUN_VERBS.has(verb);

/**
 * WHICH VERBS `/api/trade/execute` ACCEPTS WITH NO `plan_token`.
 *
 * A MIRROR OF THE BACKEND'S `trade_actions._NO_TOKEN_VERBS`, WHICH OWNS THE ANSWER, and
 * `chartapp/api/tests/test_no_token_verbs_mirror.py` asserts the two sets are EQUAL —
 * on the side that owns the value, because vitest cannot import a Python frozenset.
 *
 * WHY A THIRD SET RATHER THAN REUSING EITHER OF THE TWO ABOVE. They answer UI questions
 * and this one answers a PROTOCOL question, and the memberships genuinely differ:
 * `cancel` shows no modal (`NO_CONFIRM_VERBS`) while `close` shows one with no preview
 * (`NO_DRYRUN_VERBS`), yet BOTH send in a single request with no token. Folding the
 * protocol into `NO_DRYRUN_VERBS` would either give `cancel` a dialog or claim `close`
 * has none. What the three do share is asserted rather than assumed: the union of the two
 * one-click sets EQUALS this one, because a verb that sends inside a single click is
 * exactly a verb with no human step for a token to bind.
 *
 * THE DEFECT THIS SET EXISTS TO MAKE IMPOSSIBLE (card t_992b85d1). `close` was collapsed
 * to one press on 2026-08-25 by editing `NO_DRYRUN_VERBS` here while the backend's
 * `execute` kept demanding a token, so Andrei's press returned `400 plan_token does not
 * authorise these arguments` and closing a position was GUARANTEED to fail. The docstring
 * at the top of this file claims a duplicated backend set "can only ever hide a button,
 * never enable a write the backend would reject" — this copy did the OPPOSITE. That
 * invariant is now a test (`test_no_token_verbs_mirror.py`) rather than a comment.
 */
export const NO_TOKEN_VERBS: ReadonlySet<string> = new Set(['close', 'cancel']);

export const isNoToken = (verb: string): boolean => NO_TOKEN_VERBS.has(verb);

/**
 * WHAT THE DIALOG SAYS IT IS DOING, DERIVED FROM THE VERB IN ONE PLACE.
 *
 * Card t_c977f6d4. `index.html` declared `<button id="tp-apply">Set stop</button>` as a
 * STATIC label and nothing ever rewrote it (`grep tpApply.textContent main.ts` -> 0 hits),
 * while `tpOpenAction` set the dialog's TITLE from a `label` its five call sites each
 * passed by hand. Two independent places spelling one action, so they drifted: a dialog
 * titled "Set take profit — CC", with a field labelled "Take profit", carried a confirm
 * button reading **"Set stop"** — and that button is the whole write
 * (`/api/trade/modify`) in one press. An operator who reads the button rather than the
 * title confirms "stop" while MOVING THE TARGET. Found by reading a screenshot
 * (`/tmp/r4-9-proof/3-refused.png`) with vision; 28 mutants, 1243 vitest cases and the
 * whole pytest suite were blind to it, because no test asserted anything about the label
 * of the control that sends the order.
 *
 * So the copy is DERIVED, not passed: one verb, one entry, title and button from the same
 * row. A call site cannot supply a title that disagrees with the button because it cannot
 * supply either. Adding a verb without adding a row here fails
 * `actionCopy`'s own test rather than silently inheriting whatever the last author typed.
 *
 * NOT rewritten from the drag handler either (the card's second DO NOT): an imperative
 * `el.tpApply.textContent = …` beside `el.tpDlgTitle.textContent = …` is exactly the
 * two-writers arrangement that produced the defect, one indirection later.
 *
 * `button` is the IMPERATIVE for the press and names the leg it moves; `title` is the
 * dialog heading. They may differ in register but never in WHICH LEG — that is what
 * `oneclick.test.ts` pins per verb.
 */
export type ActionCopy = {
  /** Dialog heading, rendered as `${title} — ${symbol}`. */
  title: string;
  /** The label on the control that sends it (`#tp-apply` for one-click verbs). */
  button: string;
  /**
   * The one PRICE field this verb edits: the body key the server reads, and the label
   * above it. `null` for verbs whose dialog carries no price (`close` sends a fixed
   * `pct`).
   *
   * IT IS HERE FOR THE SAME REASON THE BUTTON IS (card t_c977f6d4, DONE item 4). The
   * field label was a fourth independently-typed string for the same leg — `'New stop'`
   * / `'Take profit'` written out at BOTH the drag call site and the table-button call
   * site — so it was one edit away from the defect that was just fixed one control over.
   * Call sites now supply only the VALUE, which is the only part that differs between
   * them (the drag's gesture price vs the resting leg from the order graph).
   */
  price: { name: string; label: string } | null;
};

const ACTION_COPY: Readonly<Record<string, ActionCopy>> = {
  'modify-sl': {
    title: 'Move stop loss',
    button: 'Move stop loss',
    price: { name: 'stop', label: 'New stop' },
  },
  'modify-tp': {
    title: 'Set take profit',
    button: 'Set take profit',
    price: { name: 'tp', label: 'Take profit' },
  },
  close: { title: 'Close position (100%, MARKET)', button: 'Close position', price: null },
  place: { title: 'Place order', button: 'Place order', price: null },
  cancel: { title: 'Cancel order', button: 'Cancel order', price: null },
};

/**
 * The copy for one verb. An UNKNOWN verb gets a label that names the verb rather than a
 * plausible-looking default: a wrong-but-confident word on this button is the defect
 * being fixed, and "Send modify-xyz" is visibly unfinished where "Set stop" was not.
 */
export function actionCopy(verb: string): ActionCopy {
  return ACTION_COPY[verb] ?? { title: `Send ${verb}`, button: `Send ${verb}`, price: null };
}

/** Which verbs have hand-written copy. Exported so a test can require the whole set. */
export const ACTION_COPY_VERBS: readonly string[] = Object.keys(ACTION_COPY);

/** What a cancelling row says. The order is STILL THERE in both states. */
export type CancelRowState = 'cancelling' | 'failed';

/**
 * The row copy, in one place so a test can pin the register. Read while trading,
 * at 10px, so: short, no jargon, and never colour alone (each state carries text).
 */
export const CANCEL_TEXT = {
  cancelling: 'Cancelling...',
  cancelled: 'Cancelled',
  failed: 'Still open - cancel failed',
} as const;

/** What was cancelled, said once, after the order is gone from the re-read book. */
export const cancelNote = (oid: string, coin: string): string =>
  `${CANCEL_TEXT.cancelled}: ${coin} order ${oid} is gone from the book`;

/**
 * MAY THIS ORDER BE SHOWN AS CANCELLED?
 *
 * Only on two independent signals agreeing, and `ok` is not one of them on its own:
 * exec_tools' `cancel` has returned `{"cancelled": false, "error": null}` for orders
 * that were already gone (root .hermes.md §0b.1), so its self-report is not proof in
 * either direction. The proof is the order's DISAPPEARANCE from a forced re-read.
 *
 *  * `ok` -- the HTTP calls succeeded and the run was not refused;
 *  * `stillResting` -- the order is still in the book after `?force=true`.
 *
 * Anything short of both means the order is still open, and the row must say so. A
 * row that claims Cancelled while the order rests is the one lie this feature could
 * tell, and it would be read as protection removed / protection still there --
 * backwards, on a stop.
 */
export function cancelOutcome(ev: { ok: boolean; stillResting: boolean }): 'cancelled' | 'failed' {
  return ev.ok && !ev.stillResting ? 'cancelled' : 'failed';
}

export type DialogPhase =
  | 'idle'          // dialog just opened
  | 'in-flight'     // a request is running
  | 'planned'       // a clean dry-run minted a token (two-phase only)
  | 'refused'       // exec_tools declined; the book is untouched
  | 'done';         // a confirmed write came back

export type DialogInput = {
  verb: string;
  phase: DialogPhase;
  /** A token from a clean dry-run, if any. */
  hasToken: boolean;
  /** Is the backend's write path armed (`/api/account/health`)? */
  armed: boolean;
};

export type ButtonState = {
  apply: { shown: boolean; enabled: boolean };
  dryrun: { shown: boolean; enabled: boolean };
  confirm: { shown: boolean; enabled: boolean };
  /** Which control the user is meant to press next, for the dialog's note line. */
  primary: 'apply' | 'dryrun' | 'confirm' | null;
};

/**
 * Which buttons exist and which are pressable.
 *
 * The invariants a test can hold this to:
 *
 *  * a REFUSAL must never leave a pressable write control — the failure mode of
 *    one-click is a user who clicked once, got a rejection, and clicks again out of
 *    habit;
 *  * nothing that writes is enabled while `armed` is false, whatever the phase;
 *  * `confirm` is never pressable without a token EXCEPT for a `NO_DRYRUN_VERBS`
 *    verb, which mints and spends its own inside the click (server-side, argv-bound,
 *    never shown to a user who cannot act on it) — and `apply` never needs one for
 *    the same reason;
 *  * the one-click and two-phase controls are mutually exclusive, so the dialog
 *    cannot offer two different write paths for the same action at once;
 *  * a verb that skips the dry-run shows NO Dry-run button. A dead control beside a
 *    live one is the clutter Andrei asked to be removed, and a button that no longer
 *    gates anything invites a press that proves nothing.
 */
export function buttonState(d: DialogInput): ButtonState {
  const oneClick = isOneClick(d.verb);
  const busy = d.phase === 'in-flight';
  // A refusal or a completed write both end the interaction: the user must change
  // an input (which resets the phase to `idle`) before anything can be sent again.
  const spent = d.phase === 'refused' || d.phase === 'done';
  const canWrite = d.armed && !busy && !spent;

  if (oneClick) {
    return {
      apply: { shown: true, enabled: canWrite },
      dryrun: { shown: false, enabled: false },
      confirm: { shown: false, enabled: false },
      primary: canWrite ? 'apply' : null,
    };
  }
  if (isNoDryRun(d.verb)) {
    // ONE PRESS, IN A MODAL. Confirm is live from the moment the dialog opens and the
    // Dry-run button is GONE — not merely optional. `hasToken` is deliberately not
    // consulted: there is no phase in which a user could hold one, because the plan
    // and the execute both run inside this press (`tpConfirmNow`).
    return {
      apply: { shown: false, enabled: false },
      dryrun: { shown: false, enabled: false },
      confirm: { shown: true, enabled: canWrite },
      primary: canWrite ? 'confirm' : null,
    };
  }
  return {
    apply: { shown: false, enabled: false },
    dryrun: { shown: true, enabled: !busy && !spent },
    confirm: { shown: true, enabled: canWrite && d.hasToken && d.phase === 'planned' },
    primary: busy || spent ? null : (d.hasToken && d.phase === 'planned' ? 'confirm' : 'dryrun'),
  };
}

/** The dialog's footer note: what will happen, or why nothing can. */
export function dialogNote(d: DialogInput, armReason?: string | null): string {
  if (!d.armed) return `writes disabled — ${armReason ?? 'no signing key'}`;
  if (d.phase === 'in-flight') {
    // THE PHASE GUARDS ARE VERB-AWARE TOO, and this one was not. `tpConfirmNow` sets
    // `in-flight` BEFORE awaiting `/api/trade/plan`, so this string is what the operator
    // reads for the whole window between the irreversible press and the venue's answer.
    // On a no-dry-run verb there is no checking phase to report: `_close` makes ZERO
    // `risk_gates` calls, so "running the safety checks" attributes a whole phase that
    // does not exist — the same false reassurance the `isNoDryRun` note branch below was
    // written to avoid, and worse, because it lands in the seconds when the operator is
    // deciding whether the order went out. Say what IS happening: the write is on the
    // wire. It stays TRUE for the two-phase and one-click verbs, which really do run
    // gates before the write (RULE 5 / RULE 3, RULE 11, RULE 4).
    return isNoDryRun(d.verb)
      ? 'no checks to run — the order is on the wire'
      : 'running the safety checks…';
  }
  if (d.phase === 'refused') return 'refused — the book was NOT changed';
  if (d.phase === 'done') return 're-read the book to confirm (done automatically)';
  if (isOneClick(d.verb)) {
    // Say plainly that one press writes. The gates are unchanged, but the user should
    // not discover the absence of a confirm step by pressing.
    //
    // Name the verb's OWN rules. This line said "RULE 5 / RULE 3" while `modify-sl` was
    // the only one-click verb; repeating that for a take-profit would credit it with
    // checks that do not run on its path — a false reassurance, which is worse than no
    // note at all.
    const rules = d.verb === 'modify-tp' ? 'RULE 11 (TP side)' : 'RULE 5 / RULE 3';
    return `one press applies this: the server checks ${rules} and refuses if unsafe`;
  }
  if (isNoDryRun(d.verb)) {
    // THE TRUTH, AND NOTHING BORROWED FROM THE ONE-CLICK LINE ABOVE. `_close` runs no
    // risk gate at all, so naming "RULE 5 / RULE 3" here would credit this press with
    // checks that do not exist on its path — a false reassurance, which is worse than
    // no note (the same defect the `modify-tp` branch above was split out to avoid).
    // What IS true is the reason there is nothing to check: a close only reduces
    // exposure. The argument validation that survives is not a risk gate and is not
    // advertised as one.
    return 'one press closes this at market — no checks, because closing only reduces risk';
  }
  return d.hasToken && d.phase === 'planned'
    ? 'dry-run clean — Confirm sends it'
    : 'dry-run first (this verb has no pre-confirm risk gate)';
}
